export const PEOPLE_ONBOARDING_QUESTION_TYPES = {
	HEADING: 'heading',
	TEXT: 'text',
	DROPDOWN: 'dropdown',
	CHECKBOX: 'checkbox',
	CHECKBOX_GROUP: 'checkboxGroup',
};

const MAX_ADDITIONAL_QUESTIONS = 40;
const MAX_QUESTION_LABEL_LENGTH = 200;
const MAX_PLACEHOLDER_LENGTH = 200;
const MAX_OPTIONS_PER_QUESTION = 50;
const MAX_OPTION_LABEL_LENGTH = 120;
const MAX_ANSWER_COMMENT_LENGTH = 500;

const toTrimmedString = (value, maxLength = 0) => {
	if (value === undefined || value === null) {
		return '';
	}

	const trimmed = String(value).trim();
	if (!trimmed) {
		return '';
	}

	if (!maxLength || trimmed.length <= maxLength) {
		return trimmed;
	}

	return trimmed.slice(0, maxLength);
};

const buildFallbackQuestionId = (index) => `question_${index + 1}`;

const normalizeQuestionId = (value, index) => {
	const trimmed = toTrimmedString(value, 80);
	if (!trimmed) {
		return buildFallbackQuestionId(index);
	}

	const normalized = trimmed.replace(/[^a-zA-Z0-9_-]/g, '_');
	return normalized || buildFallbackQuestionId(index);
};

export const normalizePeopleOnboardingQuestionType = (value) => {
	const normalized = toTrimmedString(value).toLowerCase();

	switch (normalized) {
		case 'section':
		case PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING:
			return PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING;
		case PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT:
			return PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;
		case 'select':
		case PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN:
			return PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN;
		case 'boolean':
		case PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX:
			return PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX;
		case 'checkboxgroup':
		case 'checkbox_group':
		case 'multi_checkbox':
		case PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP.toLowerCase():
			return PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP;
		default:
			return null;
	}
};

export const sanitizePeopleOnboardingQuestionOptionConfigs = (rawOptions) => {
	if (!Array.isArray(rawOptions)) {
		return [];
	}

	const seen = new Map();
	const sanitized = [];

	for (const option of rawOptions) {
		const optionLabel = toTrimmedString(
			typeof option === 'object' && option !== null
				? (option.label ?? option.value)
				: option,
			MAX_OPTION_LABEL_LENGTH,
		);

		if (!optionLabel) {
			continue;
		}

		const key = optionLabel.toLowerCase();
		const allowComment = Boolean(option && typeof option === 'object' && option.allowComment === true);
		const highlightInList = Boolean(option && typeof option === 'object' && option.highlightInList === true);

		if (seen.has(key)) {
			if (allowComment) {
				sanitized[seen.get(key)].allowComment = true;
			}
			if (highlightInList) {
				sanitized[seen.get(key)].highlightInList = true;
			}
			continue;
		}

		seen.set(key, sanitized.length);
		sanitized.push({
			label: optionLabel,
			allowComment,
			highlightInList,
		});

		if (sanitized.length >= MAX_OPTIONS_PER_QUESTION) {
			break;
		}
	}

	return sanitized;
};

export const sanitizePeopleOnboardingQuestionOptions = (rawOptions) => (
	sanitizePeopleOnboardingQuestionOptionConfigs(rawOptions).map(option => option.label)
);

const sanitizePeopleOnboardingQuestionCommentOptions = (
	rawOptions,
	rawCommentOptions,
	{ allowCommentWhenSelected = false } = {},
) => {
	const allowedOptions = sanitizePeopleOnboardingQuestionOptions(rawOptions);
	if (!allowedOptions.length) {
		return [];
	}

	const allowedOptionMap = allowedOptions.reduce((acc, option) => {
		acc.set(option.toLowerCase(), option);
		return acc;
	}, new Map());
	const seen = new Set();
	const sanitized = [];

	const addOption = (value) => {
		const optionLabel = toTrimmedString(value, MAX_OPTION_LABEL_LENGTH);
		if (!optionLabel) {
			return;
		}

		const canonicalOption = allowedOptionMap.get(optionLabel.toLowerCase());
		if (!canonicalOption) {
			return;
		}

		const optionKey = canonicalOption.toLowerCase();
		if (seen.has(optionKey)) {
			return;
		}

		seen.add(optionKey);
		sanitized.push(canonicalOption);
	};

	sanitizePeopleOnboardingQuestionOptionConfigs(rawOptions)
		.filter(option => option.allowComment)
		.forEach(option => addOption(option.label));
	sanitizePeopleOnboardingQuestionOptions(rawCommentOptions)
		.forEach(addOption);

	if (!sanitized.length && allowCommentWhenSelected) {
		allowedOptions.forEach(addOption);
	}

	return sanitized;
};

const sanitizePeopleOnboardingQuestionHighlightOptions = (
	rawOptions,
	rawHighlightOptions,
) => {
	const allowedOptions = sanitizePeopleOnboardingQuestionOptions(rawOptions);
	if (!allowedOptions.length) {
		return [];
	}

	const allowedOptionMap = allowedOptions.reduce((acc, option) => {
		acc.set(option.toLowerCase(), option);
		return acc;
	}, new Map());
	const seen = new Set();
	const sanitized = [];

	const addOption = (value) => {
		const optionLabel = toTrimmedString(value, MAX_OPTION_LABEL_LENGTH);
		if (!optionLabel) {
			return;
		}

		const canonicalOption = allowedOptionMap.get(optionLabel.toLowerCase());
		if (!canonicalOption) {
			return;
		}

		const optionKey = canonicalOption.toLowerCase();
		if (seen.has(optionKey)) {
			return;
		}

		seen.add(optionKey);
		sanitized.push(canonicalOption);
	};

	sanitizePeopleOnboardingQuestionOptionConfigs(rawOptions)
		.filter(option => option.highlightInList)
		.forEach(option => addOption(option.label));
	sanitizePeopleOnboardingQuestionOptions(rawHighlightOptions)
		.forEach(addOption);

	return sanitized;
};

export const sanitizePeopleOnboardingQuestions = (rawQuestions) => {
	if (!Array.isArray(rawQuestions)) {
		return [];
	}

	const seenIds = new Set();
	const sanitized = [];

	for (let index = 0; index < rawQuestions.length; index += 1) {
		const question = rawQuestions[index];
		if (!question || typeof question !== 'object') {
			continue;
		}

		const normalizedType = normalizePeopleOnboardingQuestionType(question.type) || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;
		const label = toTrimmedString(question.label || question.question || question.title, MAX_QUESTION_LABEL_LENGTH);
		if (!label) {
			continue;
		}

		let questionId = normalizeQuestionId(question.id, index);
		while (seenIds.has(questionId)) {
			questionId = `${questionId}_${index}`;
		}
		seenIds.add(questionId);

		const sanitizedQuestion = {
			id: questionId,
			label,
			type: normalizedType,
			required: normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING ? false : Boolean(question.required),
		};

		if (normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING) {
			sanitized.push(sanitizedQuestion);

			if (sanitized.length >= MAX_ADDITIONAL_QUESTIONS) {
				break;
			}

			continue;
		}

		if (normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT) {
			const placeholder = toTrimmedString(question.placeholder, MAX_PLACEHOLDER_LENGTH);
			if (placeholder) {
				sanitizedQuestion.placeholder = placeholder;
			}
		}

		if ([PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN, PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP].includes(normalizedType)) {
			const options = sanitizePeopleOnboardingQuestionOptions(question.options);
			if (!options.length) {
				continue;
			}

			sanitizedQuestion.options = options;
			const commentOptions = sanitizePeopleOnboardingQuestionCommentOptions(
				question.options,
				question.commentOptions,
				{ allowCommentWhenSelected: question.allowCommentWhenSelected === true },
			);
			if (commentOptions.length) {
				sanitizedQuestion.commentOptions = commentOptions;
			}
			const highlightOptions = sanitizePeopleOnboardingQuestionHighlightOptions(
				question.options,
				question.highlightOptions,
			);
			if (highlightOptions.length) {
				sanitizedQuestion.highlightOptions = highlightOptions;
			}

			if (normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
				sanitizedQuestion.allowMultiple = question.allowMultiple !== false;
			}
		}

		if (
			[PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX, PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP].includes(normalizedType) &&
			question.allowCommentWhenSelected === true
		) {
			sanitizedQuestion.allowCommentWhenSelected = true;
		}
		if (
			normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX &&
			question.highlightWhenSelected === true
		) {
			sanitizedQuestion.highlightWhenSelected = true;
		}

		sanitized.push(sanitizedQuestion);

		if (sanitized.length >= MAX_ADDITIONAL_QUESTIONS) {
			break;
		}
	}

	return sanitized;
};

const normalizeOptionValue = (value) => toTrimmedString(value, MAX_OPTION_LABEL_LENGTH);

const normalizeOptionValuesArray = (value) => {
	const rawValues = Array.isArray(value)
		? value
		: (value === undefined || value === null || value === '' ? [] : [value]);
	const seen = new Set();
	const normalized = [];

	for (const item of rawValues) {
		const nextValue = normalizeOptionValue(item);
		if (!nextValue) {
			continue;
		}

		const key = nextValue.toLowerCase();
		if (seen.has(key)) {
			continue;
		}

		seen.add(key);
		normalized.push(nextValue);
	}

	return normalized;
};

export const normalizePeopleOnboardingAnswerValue = (question, rawValue) => {
	if (!question || typeof question !== 'object') {
		return null;
	}

	const type = normalizePeopleOnboardingQuestionType(question.type) || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING) {
		return null;
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT) {
		return toTrimmedString(rawValue, MAX_QUESTION_LABEL_LENGTH);
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN) {
		const nextValue = normalizeOptionValue(rawValue);
		const allowedOptions = new Set(sanitizePeopleOnboardingQuestionOptions(question.options).map(option => option.toLowerCase()));
		return allowedOptions.has(nextValue.toLowerCase()) ? nextValue : '';
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX) {
		return Boolean(rawValue);
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
		const allowedOptions = sanitizePeopleOnboardingQuestionOptions(question.options);
		const allowedSet = new Set(allowedOptions.map(option => option.toLowerCase()));
		const normalizedValues = normalizeOptionValuesArray(rawValue)
			.filter(value => allowedSet.has(value.toLowerCase()));

		if (question.allowMultiple === false) {
			return normalizedValues[0] || '';
		}

		return normalizedValues;
	}

	return null;
};

export const isPeopleOnboardingAnswerComplete = (question, rawValue) => {
	if (!question?.required) {
		return true;
	}

	const type = normalizePeopleOnboardingQuestionType(question.type) || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;
	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING) {
		return true;
	}
	const value = normalizePeopleOnboardingAnswerValue(question, rawValue);

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX) {
		return value === true;
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
		if (question.allowMultiple === false) {
			return Boolean(value);
		}

		return Array.isArray(value) && value.length > 0;
	}

	return Boolean(toTrimmedString(value));
};

const normalizeCommentOptionSet = (question) => {
	const explicitCommentOptions = sanitizePeopleOnboardingQuestionOptions(question?.commentOptions);
	if (explicitCommentOptions.length) {
		return new Set(explicitCommentOptions.map(option => option.toLowerCase()));
	}

	if (question?.allowCommentWhenSelected === true) {
		return new Set(sanitizePeopleOnboardingQuestionOptions(question?.options).map(option => option.toLowerCase()));
	}

	return new Set();
};

export const doesPeopleOnboardingAnswerAllowComment = (question, rawValue) => {
	if (!question || typeof question !== 'object') {
		return false;
	}

	const type = normalizePeopleOnboardingQuestionType(question.type) || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;
	const normalizedValue = normalizePeopleOnboardingAnswerValue(question, rawValue);

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX) {
		return question.allowCommentWhenSelected === true && normalizedValue === true;
	}

	const commentOptionSet = normalizeCommentOptionSet(question);
	if (!commentOptionSet.size) {
		return false;
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN) {
		return Boolean(normalizedValue) && commentOptionSet.has(String(normalizedValue).toLowerCase());
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
		const selectedValues = question.allowMultiple === false
			? (normalizedValue ? [normalizedValue] : [])
			: (Array.isArray(normalizedValue) ? normalizedValue : []);

		return selectedValues.some(value => commentOptionSet.has(String(value).toLowerCase()));
	}

	return false;
};

export const isPeopleOnboardingAnswerCommentComplete = (question, rawValue, rawComment) => {
	const normalizedValue = normalizePeopleOnboardingAnswerValue(question, rawValue);
	if (!doesPeopleOnboardingAnswerAllowComment(question, normalizedValue)) {
		return true;
	}

	return Boolean(toTrimmedString(rawComment, MAX_ANSWER_COMMENT_LENGTH));
};

export const normalizePeopleOnboardingAnswerComment = (
	question,
	rawComment,
	normalizedValue,
	{
		allowOptionalComment = false,
		maxLength = MAX_ANSWER_COMMENT_LENGTH,
	} = {},
) => {
	if (!question || typeof question !== 'object') {
		return '';
	}
	const resolvedMaxLength = Number.isInteger(maxLength) && maxLength > 0
		? maxLength
		: MAX_ANSWER_COMMENT_LENGTH;

	const type = normalizePeopleOnboardingQuestionType(question.type) || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;
	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING) {
		return '';
	}

	if (allowOptionalComment) {
		return toTrimmedString(rawComment, resolvedMaxLength);
	}

	if (![
		PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX,
		PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP,
		PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN,
	].includes(type)) {
		return '';
	}

	if (!doesPeopleOnboardingAnswerAllowComment(question, normalizedValue)) {
		return '';
	}

	return toTrimmedString(rawComment, resolvedMaxLength);
};

export const doesPeopleOnboardingAnswerTriggerHighlight = (question, rawValue) => {
	if (!question || typeof question !== 'object') {
		return false;
	}

	const type = normalizePeopleOnboardingQuestionType(question.type) || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;
	const normalizedValue = normalizePeopleOnboardingAnswerValue(question, rawValue);

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX) {
		return question.highlightWhenSelected === true && normalizedValue === true;
	}

	const highlightOptions = sanitizePeopleOnboardingQuestionOptions(question?.highlightOptions);
	if (!highlightOptions.length) {
		return false;
	}

	const highlightOptionSet = new Set(highlightOptions.map(option => option.toLowerCase()));

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN) {
		return Boolean(normalizedValue) && highlightOptionSet.has(String(normalizedValue).toLowerCase());
	}

	if (type === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
		const selectedValues = question.allowMultiple === false
			? (normalizedValue ? [normalizedValue] : [])
			: (Array.isArray(normalizedValue) ? normalizedValue : []);

		return selectedValues.some(value => highlightOptionSet.has(String(value).toLowerCase()));
	}

	return false;
};

export const resolvePeopleOnboardingAnswerValueLabel = ({
	type,
	allowMultiple = false,
	value,
}) => {
	const normalizedType = normalizePeopleOnboardingQuestionType(type) || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;

	if (normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING) {
		return '';
	}

	if (normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX) {
		return value === true ? 'Yes' : 'No';
	}

	if (normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
		if (allowMultiple === false) {
			const selectedValue = String(value || '').trim();
			return selectedValue || 'No selection';
		}

		const selectedValues = Array.isArray(value)
			? value.map(item => String(item || '').trim()).filter(Boolean)
			: [];
		return selectedValues.length ? selectedValues.join(', ') : 'No selection';
	}

	if (normalizedType === PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN) {
		const selectedValue = String(value || '').trim();
		return selectedValue || 'No selection';
	}

	const textValue = String(value || '').trim();
	return textValue || 'No response';
};
